import React, { useState, useEffect, useCallback, useRef } from 'react';
import { portabilityTermsService, clientsService, contractsService } from '../services/api';
import { fmtDate, getTodayStr } from '../utils/formatters';
import { Icon } from './shared/Icon';
import CascaDeModal from './shared/CascaDeModal';
import { DocumentViewerModal, DocViewerInfoItem } from './shared/DocumentViewerModal';
import { useConfirmarPerigo, useConfirm, usePedirTexto } from './shared/ConfirmDialog';
import { useAviso } from './shared/Aviso';
import { relatoDoLembrete, relatoDaFalhaDoLembrete } from '../utils/lembrete';
import { campo, useEstadoDaCarga, EstadoDaLista, A_PROPRIA_LISTA_DIZ_O_VAZIO, textos, mensagemDoErroDeCarga } from '../design';
import { TONE_TEXT, type StatusTone } from '../utils/statusColors';
import { CELULA, CascaDaLista, TabelaDaLista, CabecalhoDaLista, ColunaDaLista, LinhaDaLista, CelulaStatus, CelulaAcoes, CartaoDoTopo } from './shared/lista/PecasDaLista';
import { confirmacaoDeEmitirTermo, fraseDosDdrsQueNaoCarregaram, fraseDoClienteQueNaoCarregou, rotuloDoPdf } from '../utils/administrativo';
import { nomeDeExibicao } from '../utils/nomeDeExibicao';


interface PortabilityTerm {
  id: number;
  numero: string;
  ano: number;
  status: string;
  clientId: number;
  client?: { id: number; tradeName?: string; companyName?: string; name?: string };
  contractId?: number;
  contract?: { id: number; code?: string; contractNumber?: string };
  razaoSocial: string;
  cnpj: string;
  endereco?: string;
  operadoraDoadora?: string;
  numeros: Array<{ tipo: string; numero?: string; numeroInicial?: string; numeroFinal?: string; numerosAvulsos?: string[] }>;
  signatarioNome: string;
  signatarioCpf?: string;
  signatarioEmail?: string;
  dataAutorizacao?: string;
  nomeFantasia?: string;
  clicksignDocumentKey?: string;
  clicksignSignatureUrl?: string;
  signers?: Array<{ name: string; email: string; cpf?: string; role?: string; signedAt?: string }>;
  signedAt?: string;
  sentForSignatureAt?: string;
  createdAt: string;
}

/**
 * O status pelo TOM (23/10/2026, padrão da Central, o mesmo de Ofícios): o andamento normal (Rascunho, Emitido, Assinado) é
 * neutro; Aguardando assinatura pede acompanhamento (âmbar); Cancelado nunca valeu (vermelho); Revogado existiu e deixou de
 * valer (âmbar).
 */
const TOM_DO_STATUS: Record<string, StatusTone> = {
  'Rascunho': 'neutral',
  'Emitido': 'neutral',
  'Aguardando Assinatura': 'warning',
  'Assinado': 'neutral',
  'Cancelado': 'danger',
  'Revogado': 'warning',
};

const countNumeros = (numeros: PortabilityTerm['numeros']): number => {
  let total = 0;
  for (const n of numeros || []) {
    if (n.tipo === 'unico') total += 1;
    else if (n.tipo === 'sequencial') {
      const ini = parseInt((n.numeroInicial || '').replace(/\D/g, ''), 10);
      const fim = parseInt((n.numeroFinal || '').replace(/\D/g, ''), 10);
      total += (!isNaN(ini) && !isNaN(fim)) ? Math.abs(fim - ini) + 1 : 1;
    } else if (n.tipo === 'avulso') total += (n.numerosAvulsos || []).length;
  }
  return total;
};

export default function PortabilityTermsTab() {
  const confirmarPerigo = useConfirmarPerigo();
  const confirmar = useConfirm();
  const pedirTexto = usePedirTexto();
  // D6 (10/10/2026): os 16 alert() viram o aviso do HUB.
  const avisar = useAviso();
  // O lembrete diz QUEM recebeu: os dois botões (linha e painel) diziam "reenviada com sucesso" sem ler a resposta
  // (fase 0, 23/10/2026).
  const reenviarLembrete = async (id: number) => {
    try {
      const { texto, tom } = relatoDoLembrete(await portabilityTermsService.resendNotification(id));
      avisar(texto, tom);
    } catch (e: any) {
      const { texto, tom } = relatoDaFalhaDoLembrete(e);
      avisar(texto, tom);
    }
  };
  const [terms, setTerms] = useState<PortabilityTerm[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [contracts, setContracts] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('Todos');
  const [modal, setModal] = useState<'create' | 'edit' | 'view' | 'signature' | null>(null);
  const [selectedTerm, setSelectedTerm] = useState<PortabilityTerm | null>(null);
  const [saving, setSaving] = useState(false);
  const [termHistory, setTermHistory] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [pdfPreview, setPdfPreview] = useState<string | null>(null);
  const [previewErro, setPreviewErro] = useState<string | null>(null);
  const [historicoErro, setHistoricoErro] = useState<string | null>(null);
  const [draftDDRs, setDraftDDRs] = useState<any[]>([]);
  // A lista de DDRs e o cadastro do cliente que não carregaram: o operador digitava à mão sem saber.
  const [ddrsErro, setDdrsErro] = useState<string | null>(null);
  const [clienteErro, setClienteErro] = useState<string | null>(null);
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [pasteMode, setPasteMode] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const clientDropdownRef = useRef<HTMLDivElement>(null);

  const emptyForm: any = {
    clientId: '',
    contractId: '',
    razaoSocial: '',
    cnpj: '',
    endereco: '',
    operadoraDoadora: '',
    numeros: [] as Array<{ tipo: string; numero?: string; numeroInicial?: string; numeroFinal?: string; numerosAvulsos?: string[] }>,
    signatarioNome: '',
    signatarioCpf: '',
    signatarioEmail: '',
    dataAutorizacao: getTodayStr(),
    observacoes: '',
  };

  const [form, setForm] = useState<any>(emptyForm);
  const [signerForm, setSignerForm] = useState({
    name: '',
    email: '',
    cpf: '',
    role: '',
    authType: 'email' as 'email' | 'icp_brasil' | 'whatsapp',
    phone: '',
  });

  // D5 (10/10/2026): a carga calava; com a API fora a lista dizia "Nenhum termo" e o Novo Termo seguia ativo.
  const carga = useEstadoDaCarga();
  const rodarCarga = carga.rodar;
  const loadData = useCallback(async () => {
    await rodarCarga(async () => {
      const [termsData, clientsData, contractsData] = await Promise.all([
        portabilityTermsService.getAll(),
        clientsService.getAll(),
        contractsService.getAll(),
      ]);
      setTerms(Array.isArray(termsData) ? termsData : []);
      setClients(Array.isArray(clientsData) ? clientsData : []);
      setContracts(Array.isArray(contractsData) ? contractsData : []);
    });
  }, [rodarCarga]);
  const loading = carga.estado === 'carregando';
  const temFonte = carga.atualizadoEm !== null;
  const kpi = (n: number) => (temFonte ? n : textos.numeroSemFonte);

  useEffect(() => { loadData(); }, [loadData]);

  const totalTerms = terms.length;
  const totalRascunhos = terms.filter(t => t.status === 'Rascunho').length;
  const totalAguardando = terms.filter(t => t.status === 'Aguardando Assinatura').length;
  const totalAssinados = terms.filter(t => t.status === 'Assinado').length;

  const filtered = terms.filter((t) => {
    const matchSearch =
      t.numero.toLowerCase().includes(search.toLowerCase()) ||
      t.razaoSocial.toLowerCase().includes(search.toLowerCase()) ||
      (t.operadoraDoadora || '').toLowerCase().includes(search.toLowerCase()) ||
      (t.client?.name || t.client?.tradeName || t.client?.companyName || '').toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'Todos' || t.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const handleClientChange = async (clientId: string) => {
    setDdrsErro(null);
    setClienteErro(null);
    if (!clientId) {
      setForm((prev: any) => ({ ...prev, clientId: '' }));
      setDraftDDRs([]);
      return;
    }
    try {
      const data = await portabilityTermsService.getDraftData(parseInt(clientId));
      setDraftDDRs(data.ddrs || []);

      // Auto-detect carrier from DDRs
      const carriers = Array.from(new Set((data.ddrs || []).map((d: any) => d.carrier).filter(Boolean)));
      const mainCarrier = carriers.length === 1 ? carriers[0] : '';

      setForm((prev: any) => ({
        ...prev,
        clientId,
        razaoSocial: data.razaoSocial || '',
        cnpj: data.cnpj || '',
        endereco: data.endereco || '',
        operadoraDoadora: mainCarrier || prev.operadoraDoadora,
      }));
    } catch (e) {
      setForm((prev: any) => ({ ...prev, clientId }));
      setClienteErro(mensagemDoErroDeCarga(e));
    }
  };

  // Close client dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(e.target as Node)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCreate = () => {
    setForm(emptyForm);
    setSelectedTerm(null);
    setDraftDDRs([]);
    setDdrsErro(null);
    setClienteErro(null);
    setClientSearch('');
    setModal('create');
  };

  const handleEdit = async (term: PortabilityTerm) => {
    setForm({
      clientId: term.clientId || '',
      contractId: term.contractId || '',
      razaoSocial: term.razaoSocial || '',
      cnpj: term.cnpj || '',
      endereco: term.endereco || '',
      operadoraDoadora: term.operadoraDoadora || '',
      numeros: term.numeros || [],
      signatarioNome: term.signatarioNome || '',
      signatarioCpf: term.signatarioCpf || '',
      signatarioEmail: term.signatarioEmail || '',
      dataAutorizacao: term.dataAutorizacao ? term.dataAutorizacao.split('T')[0] : '',
      observacoes: '',
    });
    setSelectedTerm(term);
    const editClient = clients.find((c: any) => c.id === term.clientId);
    setClientSearch(editClient ? (editClient.name || editClient.companyName) : '');
    // Load DDRs for the client
    setDdrsErro(null);
    setClienteErro(null);
    if (term.clientId) {
      try {
        const data = await portabilityTermsService.getDraftData(term.clientId);
        setDraftDDRs(data.ddrs || []);
      } catch (e) { setDraftDDRs([]); setDdrsErro(mensagemDoErroDeCarga(e)); }
    }
    setModal('edit');
  };

  const handleView = async (term: PortabilityTerm) => {
    setSelectedTerm(term);
    setPdfPreview(null);
    setPreviewErro(null);
    setHistoricoErro(null);
    setTermHistory([]);
    setModal('view');
    // O histórico era carregado só quando alguém clicava em "Ver Historico", e o botão só
    // existia se o termo tivesse sido enviado para assinatura. Carrega sempre: em termo
    // assinado, a pergunta que a tela precisa responder é exatamente essa.
    loadTimeline(term.id);
    try {
      const result = await portabilityTermsService.getPdfBase64(term.id);
      setPdfPreview(result.base64);
    } catch (e) {
      setPreviewErro(mensagemDoErroDeCarga(e));
    }
  };

  const loadTimeline = async (id: number) => {
    setHistoryLoading(true);
    try {
      setTermHistory(await portabilityTermsService.getHistory(id));
    } catch (e) {
      // Acessório: falhar aqui não pode esconder o documento, mas a lateral diz que não carregou.
      setTermHistory([]);
      setHistoricoErro(mensagemDoErroDeCarga(e));
    } finally {
      setHistoryLoading(false);
    }
  };

  /** Painel lateral do modal do olho. */
  const infoDoTermo = (term: PortabilityTerm): DocViewerInfoItem[] => {
    const itens: DocViewerInfoItem[] = [
      {
        label: 'Status',
        value: term.status,
        tone: term.status === 'Assinado' ? 'success'
          : term.status === 'Aguardando Assinatura' ? 'warning'
          : term.status === 'Cancelado' ? 'danger'
          : 'default',
      },
      { label: 'Cliente', value: term.nomeFantasia || term.razaoSocial },
      { label: 'Operadora', value: term.operadoraDoadora || '—' },
      { label: 'Números', value: String(countNumeros(term.numeros)) },
      { label: 'Criado em', value: fmtDate(term.createdAt) },
    ];
    if (term.signedAt) itens.push({ label: 'Assinado em', value: fmtDate(term.signedAt), tone: 'success' });
    return itens;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = {
        ...form,
        clientId: form.clientId ? parseInt(form.clientId) : undefined,
        contractId: form.contractId ? parseInt(form.contractId) : null,
        operadoraDoadora: form.operadoraDoadora === '_outro' ? '' : form.operadoraDoadora,
      };
      if (selectedTerm) {
        await portabilityTermsService.update(selectedTerm.id, data);
      } else {
        await portabilityTermsService.create(data);
      }
      setModal(null);
      loadData();
    } catch (e: any) {
      const msg = e.response?.data?.message;
      avisar('Erro ao salvar: ' + (Array.isArray(msg) ? msg.join('\n') : msg || e.message), 'error');
    }
    setSaving(false);
  };

  const handleDelete = async (term: PortabilityTerm) => {
    if (!await confirmarPerigo(['Excluir o rascunho?', `${term.numero} de ${term.razaoSocial}.`].join('\n\n'))) return;
    try {
      await portabilityTermsService.delete(term.id);
      loadData();
    } catch (e: any) { avisar('Erro ao excluir: ' + (e.response?.data?.message || e.message), 'error'); }
  };

  const handleEmit = async (term: PortabilityTerm) => {
    if (!await confirmar(confirmacaoDeEmitirTermo(term, countNumeros(term.numeros)))) return;
    try {
      await portabilityTermsService.emit(term.id);
      loadData();
    } catch (e: any) { avisar('Erro ao emitir: ' + (e.response?.data?.message || e.message), 'error'); }
  };

  const handleCancel = async (term: PortabilityTerm) => {
    if (!await confirmarPerigo(['Cancelar o termo?', `${term.numero} de ${term.razaoSocial}. ` + (term.status === 'Aguardando Assinatura' ? 'O envelope no ClickSign é cancelado. ' : '') + 'O número não se reaproveita.'].join('\n\n'))) return;
    try {
      await portabilityTermsService.cancel(term.id);
      loadData();
    } catch (e: any) { avisar('Erro ao cancelar: ' + (e.response?.data?.message || e.message), 'error'); }
  };

  // D7 (10/10/2026): o backend tinha o revoke e a tela não tinha o botão: termo assinado não se revogava.
  const handleRevoke = async (term: PortabilityTerm) => {
    const motivo = await pedirTexto({
      title: 'Revogar o termo ' + term.numero + '?',
      consequence: 'O termo continua existindo, com a assinatura e o PDF assinado preservados. O que muda é que ele passa a constar como sem validade.',
      label: 'Motivo',
      obrigatorio: true,
      validar: (t) => (t.length < 10 ? 'Pelo menos 10 caracteres.' : null),
      danger: true,
      confirmLabel: 'Revogar',
    });
    if (motivo === null) return;
    try {
      await portabilityTermsService.revoke(term.id, motivo.trim());
      loadData();
    } catch (e: any) { avisar('Erro ao revogar: ' + (e.response?.data?.message || e.message), 'error'); }
  };

  const handleOpenSignature = (term: PortabilityTerm) => {
    setSelectedTerm(term);
    setSignerForm({
      name: term.signatarioNome || '',
      email: term.signatarioEmail || '',
      cpf: term.signatarioCpf || '',
      role: '',
      authType: 'email',
      phone: '',
    });
    setModal('signature');
  };

  const handleSendForSignature = async () => {
    if (!selectedTerm) return;
    if (!signerForm.name || !signerForm.email) {
      avisar('Preencha nome e email do signatario', 'error');
      return;
    }
    if (signerForm.authType === 'whatsapp' && !signerForm.phone) {
      avisar('Preencha o numero de WhatsApp do signatario', 'error');
      return;
    }
    setSaving(true);
    try {
      await portabilityTermsService.sendForSignature(selectedTerm.id, [signerForm]);
      setModal(null);
      loadData();
      avisar('Termo enviado para assinatura com sucesso!', 'success');
    } catch (e: any) { avisar('Erro ao enviar para assinatura: ' + (e.response?.data?.message || e.message), 'error'); }
    setSaving(false);
  };

  const handleCheckStatus = async (term: PortabilityTerm) => {
    try {
      const result = await portabilityTermsService.getSignatureStatus(term.id);
      const allSigned = result.signers?.every((s: any) => s.status === 'signed');
      if (allSigned) {
        avisar('Documento assinado! Status atualizado.', 'success');
      } else {
        const pending = result.signers?.filter((s: any) => s.status === 'pending').map((s: any) => s.name).join(', ');
        avisar('Aguardando assinatura de: ' + (pending || 'signatarios'), 'info');
      }
      loadData();
    } catch (e: any) { avisar('Erro ao verificar status: ' + (e.response?.data?.message || e.message), 'error'); }
  };

  const handleDownloadPdf = async (term: PortabilityTerm) => {
    try {
      const result = await portabilityTermsService.getPdfBase64(term.id);
      const link = document.createElement('a');
      link.href = result.base64;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e: any) { avisar('Erro ao baixar PDF: ' + (e.response?.data?.message || e.message), 'error'); }
  };

  // Toggle DDR in the numeros array
  const toggleDDR = (ddr: any) => {
    const existing = form.numeros.find((n: any) => n.numero === ddr.phoneNumber);
    if (existing) {
      setForm({ ...form, numeros: form.numeros.filter((n: any) => n.numero !== ddr.phoneNumber) });
    } else {
      setForm({ ...form, numeros: [...form.numeros, { tipo: 'unico', numero: ddr.phoneNumber }] });
    }
  };

  // Add manual number
  const addManualNumber = (tipo: string) => {
    if (tipo === 'sequencial') {
      setForm({ ...form, numeros: [...form.numeros, { tipo: 'sequencial', numeroInicial: '', numeroFinal: '' }] });
    } else if (tipo === 'avulso') {
      setForm({ ...form, numeros: [...form.numeros, { tipo: 'avulso', numerosAvulsos: [''] }] });
    } else {
      setForm({ ...form, numeros: [...form.numeros, { tipo: 'unico', numero: '' }] });
    }
  };

  const removeNumero = (idx: number) => {
    setForm({ ...form, numeros: form.numeros.filter((_: any, i: number) => i !== idx) });
  };

  const updateNumero = (idx: number, field: string, value: any) => {
    const updated = [...form.numeros];
    updated[idx] = { ...updated[idx], [field]: value };
    setForm({ ...form, numeros: updated });
  };

  const parseNumberList = (text: string) => {
    const brutos = text.split(/[\n,;]+/).map(s => s.replace(/\D/g, '')).filter(s => s.length >= 8);
    if (brutos.length === 0) return;

    // Duplicata era mantida: um número repetido não satisfaz `cur === prev + 1`, então
    // fechava e reabria a faixa no mesmo valor, gerando DUAS entradas idênticas. Isso
    // inflava a coluna "Qtd Números" e listava o número duas vezes no termo enviado à
    // operadora doadora.
    const nums = Array.from(new Set(brutos));

    // Ordenação NUMÉRICA. `localeCompare` compara como texto, o que só funciona se todos os
    // números tiverem o mesmo comprimento: misturar fixo de 10 e celular de 11 dígitos
    // embaralhava a lista e quebrava a detecção de faixa sequencial.
    nums.sort((a, b) => (a.length - b.length) || a.localeCompare(b));
    const entries: any[] = [];
    let rangeStart = nums[0];
    let rangePrev = nums[0];
    for (let i = 1; i <= nums.length; i++) {
      const cur = nums[i];
      const prevNum = parseInt(rangePrev, 10);
      const curNum = cur ? parseInt(cur, 10) : NaN;
      if (curNum === prevNum + 1) {
        rangePrev = cur;
      } else {
        if (rangeStart === rangePrev) {
          entries.push({ tipo: 'unico', numero: rangeStart });
        } else {
          entries.push({ tipo: 'sequencial', numeroInicial: rangeStart, numeroFinal: rangePrev });
        }
        rangeStart = cur;
        rangePrev = cur;
      }
    }
    setForm({ ...form, numeros: [...form.numeros, ...entries] });
    setPasteMode(false);
    setPasteText('');
  };

  if (loading && !temFonte) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-slate-400'></div>
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      {/* Summary Cards */}
      {/* Os cartões do topo (padrão das listas, 23/10/2026), como em Ofícios. */}
      <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
        <CartaoDoTopo rotulo='Total' valor={kpi(totalTerms)} sub='Termos de portabilidade' ativo={statusFilter === 'Todos'} onClick={() => setStatusFilter('Todos')} />
        <CartaoDoTopo rotulo='Rascunhos' valor={kpi(totalRascunhos)} sub='Pendentes de emissão' ativo={statusFilter === 'Rascunho'} onClick={() => setStatusFilter(statusFilter === 'Rascunho' ? 'Todos' : 'Rascunho')} />
        <CartaoDoTopo rotulo='Aguardando' valor={kpi(totalAguardando)} sub='Aguardando assinatura' barra={temFonte && totalAguardando > 0 ? 'warning' : null} ativo={statusFilter === 'Aguardando Assinatura'} onClick={() => setStatusFilter(statusFilter === 'Aguardando Assinatura' ? 'Todos' : 'Aguardando Assinatura')} />
        <CartaoDoTopo rotulo='Assinados' valor={kpi(totalAssinados)} sub='Termos concluídos' ativo={statusFilter === 'Assinado'} onClick={() => setStatusFilter(statusFilter === 'Assinado' ? 'Todos' : 'Assinado')} />
      </div>

      {/* Search and Filters */}
      <div className='bg-central-cartao rounded-central border border-central-borda p-4'>
        <div className='flex flex-col md:flex-row gap-4'>
          <div className='relative flex-1'>
            <Icon name='search' className='absolute left-3 top-3 w-5 h-5 text-slate-500' />
            <input
              type='text'
              placeholder='Buscar por numero, cliente, operadora...'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`w-full pl-10 pr-4 py-2.5 placeholder-slate-500 rounded-lg focus:outline-none ${campo}`}
            />
          </div>

          <div className='flex gap-2'>
            {['Todos', 'Rascunho', 'Emitido', 'Aguardando Assinatura', 'Assinado'].map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                  statusFilter === status
                    ? 'bg-slate-600 text-white font-semibold border border-slate-500'
                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700 border border-slate-700'
                }`}
              >
                {status === 'Aguardando Assinatura' ? 'Aguardando' : status}
              </button>
            ))}
          </div>

          <button
            onClick={() => loadData()}
            disabled={loading}
            title='Atualizar lista'
            className='flex items-center justify-center px-3 py-2.5 bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 border border-slate-700 disabled:opacity-50'
          >
            <Icon name='refresh' className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>

          {carga.estado === 'pronto' && <button
            onClick={handleCreate}
            className='flex items-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium whitespace-nowrap'
          >
            <Icon name='plus' className='w-5 h-5' />
            Novo Termo
          </button>}
        </div>
      </div>

      {/* Table */}
      <EstadoDaLista carga={carga} temDados={temFonte} oQue='os termos de portabilidade' onTentarDeNovo={loadData} vazio={A_PROPRIA_LISTA_DIZ_O_VAZIO}>
      <CascaDaLista>
        <TabelaDaLista>
          <CabecalhoDaLista>
            <ColunaDaLista rotulo='Número' largura='w-36' />
            <ColunaDaLista rotulo='Cliente' />
            <ColunaDaLista rotulo='Operadora Doadora' largura='w-40' />
            <ColunaDaLista rotulo='Qtd Números' largura='w-28' alinhamento='centro' />
            <ColunaDaLista rotulo='Status' largura='w-40' alinhamento='centro' />
            <ColunaDaLista rotulo='Data' largura='w-28' />
            {/* As de uso diário à vista; excluir, cancelar e revogar no ⋯, em perigo (como em Ofícios). */}
            <ColunaDaLista rotulo='Ações' largura='w-56' alinhamento='direita' />
          </CabecalhoDaLista>
          <tbody className='divide-y divide-central-borda'>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className='px-4 py-12 text-center text-slate-500'>
                  Nenhum termo de portabilidade encontrado
                </td>
              </tr>
            ) : (
              filtered.map((term) => {
                const encerrar = [
                  ...(term.status === 'Rascunho' ? [{ label: 'Excluir', onClick: () => handleDelete(term), danger: true }] : []),
                  ...(term.status === 'Aguardando Assinatura' ? [{ label: 'Cancelar', onClick: () => handleCancel(term), danger: true }] : []),
                  ...(term.status === 'Assinado' ? [{ label: 'Revogar (termo assinado que deixou de valer)', onClick: () => handleRevoke(term), danger: true }] : []),
                ];
                const aVista = <>
                        <button onClick={() => handleView(term)} className='p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg' title='Visualizar PDF'>
                          <Icon name='eye' className='w-4 h-4' />
                        </button>
                        <button onClick={() => handleDownloadPdf(term)} className='p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg' title={rotuloDoPdf(term.status).titulo}>
                          <Icon name='download' className='w-4 h-4' />
                        </button>
                        {term.status === 'Rascunho' && (
                          <>
                            <button onClick={() => handleEdit(term)} className='p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg' title='Editar'>
                              <Icon name='edit' className='w-4 h-4' />
                            </button>
                            <button onClick={() => handleEmit(term)} className='p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg' title='Emitir'>
                              <Icon name='check' className='w-4 h-4' />
                            </button>
                          </>
                        )}
                        {(term.status === 'Rascunho' || term.status === 'Emitido') && (
                          <button onClick={() => handleOpenSignature(term)} className='p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg' title='Enviar para Assinatura'>
                            <Icon name='send' className='w-4 h-4' />
                          </button>
                        )}
                        {term.status === 'Aguardando Assinatura' && (
                          <>
                            <button onClick={() => handleCheckStatus(term)} className='p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg' title='Verificar Status da Assinatura'>
                              <Icon name='refresh' className='w-4 h-4' />
                            </button>
                            <button onClick={() => reenviarLembrete(term.id)} className='p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg' title='Reenviar Email de Assinatura'>
                              <Icon name='send' className='w-4 h-4' />
                            </button>
                          </>
                        )}

                      </>;
                return (
                  <LinhaDaLista key={term.id} apagada={term.status === 'Cancelado'}>
                    <td className={`${CELULA} whitespace-nowrap`}>
                      <span className='font-mono text-sm text-central-texto'>{term.numero}</span>
                    </td>
                    <td className={CELULA}>
                      <div className='text-central-texto text-sm font-medium truncate' title={term.razaoSocial}>{term.razaoSocial}</div>
                      {term.client && (
                        <div className='text-slate-500 text-xs truncate'>{term.client.name || term.client.tradeName || term.client.companyName}</div>
                      )}
                    </td>
                    <td className={`${CELULA} text-slate-300 text-sm`}>{term.operadoraDoadora || '—'}</td>
                    <td className={`${CELULA} text-center text-sm text-central-texto tabular-nums`}>{countNumeros(term.numeros)}</td>
                    <CelulaStatus texto={term.status} tom={TOM_DO_STATUS[term.status] || 'neutral'} />
                    <td className={`${CELULA} text-slate-400 text-sm`}>{fmtDate(term.createdAt)}</td>
                    {encerrar.length > 0 ? (
                      <CelulaAcoes rotuloDoMenu={`Mais ações do termo ${term.numero}`} itens={encerrar} antes={aVista} />
                    ) : (
                      <td className={CELULA}><div className='flex justify-end flex-nowrap gap-0.5'>{aVista}</div></td>
                    )}
                  </LinhaDaLista>
                );
              })
            )}
          </tbody>
        </TabelaDaLista>
      </CascaDaLista>
      </EstadoDaLista>

      {/* Modal Create/Edit */}
      {(modal === 'create' || modal === 'edit') && (
        // Editor: não fecha ao clicar fora, como já era. Sai a altura chumbada do
        // corpo (max-h calc(90vh-180px)); a primária roxa vira o laranja.
        <CascaDeModal
          largura='lg'
          titulo={modal === 'create' ? 'Novo Termo de Portabilidade' : 'Editar Termo de Portabilidade'}
          onClose={() => setModal(null)}
          corpoClassName='p-6 space-y-6'
          rodape={
            <div className='ml-auto flex gap-3'>
              <button onClick={() => setModal(null)} className='px-4 py-2.5 text-sm font-medium text-slate-300 bg-slate-700 rounded-lg hover:bg-slate-600'>
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.clientId || !form.razaoSocial || !form.cnpj || !form.signatarioNome || form.numeros.length === 0}
                className='px-4 py-2.5 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-strong disabled:opacity-50'
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          }
        >
              {/* Cliente e Contrato */}
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <div ref={clientDropdownRef} className='relative'>
                  <label className='block text-sm font-medium text-slate-300 mb-2'>Cliente *</label>
                  <input
                    type='text'
                    value={clientSearch}
                    onChange={(e) => { setClientSearch(e.target.value); setShowClientDropdown(true); if (!e.target.value) { handleClientChange(''); } }}
                    onFocus={() => setShowClientDropdown(true)}
                    placeholder='Buscar cliente...'
                    className={`w-full px-4 py-2.5 ${campo} rounded-lg focus:outline-none`}
                  />
                  {showClientDropdown && (
                    <div className='absolute z-50 w-full mt-1 bg-central-elevado border border-slate-600 rounded-lg shadow-xl max-h-60 overflow-y-auto'>
                      {/* [...clients] e não clients.sort(): sort() muta o state em render. */}
                      {[...clients]
                        .sort((a: any, b: any) => nomeDeExibicao(a).localeCompare(nomeDeExibicao(b)))
                        .filter((c: any) => {
                          if (!clientSearch) return true;
                          const q = clientSearch.toLowerCase();
                          return (c.name || '').toLowerCase().includes(q) || (c.tradeName || '').toLowerCase().includes(q) || (c.companyName || '').toLowerCase().includes(q) || (c.cnpj || '').includes(q);
                        })
                        .map((c: any) => (
                          <button
                            key={c.id}
                            type='button'
                            onClick={() => { handleClientChange(String(c.id)); setClientSearch(nomeDeExibicao(c)); setShowClientDropdown(false); }}
                            className={'w-full text-left px-4 py-2 hover:bg-slate-700 text-sm ' + (String(form.clientId) === String(c.id) ? 'bg-slate-700 text-white font-semibold' : 'text-slate-300')}
                          >
                            {nomeDeExibicao(c)}
                          </button>
                        ))}
                      {clients.filter((c: any) => { if (!clientSearch) return true; const q = clientSearch.toLowerCase(); return (c.name || '').toLowerCase().includes(q) || (c.tradeName || '').toLowerCase().includes(q) || (c.companyName || '').toLowerCase().includes(q) || (c.cnpj || '').includes(q); }).length === 0 && (
                        <div className='px-4 py-3 text-sm text-slate-500'>Nenhum cliente encontrado</div>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <label className='block text-sm font-medium text-slate-300 mb-2'>Assinatura (opcional)</label>
                  <select
                    value={form.contractId}
                    onChange={(e) => setForm({ ...form, contractId: e.target.value })}
                    className={`w-full px-4 py-2.5 ${campo} rounded-lg focus:outline-none`}
                  >
                    <option value=''>Nenhuma</option>
                    {contracts
                      .filter((c: any) => !form.clientId || c.clientId === parseInt(form.clientId))
                      .map((c: any) => (
                        <option key={c.id} value={c.id}>
                          #{c.id} - {c.client?.name || c.client?.companyName || c.contractNumber || c.code || 'Assinatura'}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              {/* Dados auto-preenchidos */}
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <div>
                  <label className='block text-sm font-medium text-slate-300 mb-2'>Razao Social *</label>
                  <input type='text' value={form.razaoSocial} onChange={(e) => setForm({ ...form, razaoSocial: e.target.value })} className={`w-full px-4 py-2.5 ${campo} rounded-lg focus:outline-none`} required />
                </div>
                <div>
                  <label className='block text-sm font-medium text-slate-300 mb-2'>CNPJ *</label>
                  <input type='text' value={form.cnpj} onChange={(e) => setForm({ ...form, cnpj: e.target.value })} className={`w-full px-4 py-2.5 ${campo} rounded-lg focus:outline-none`} required />
                </div>
              </div>

              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <div>
                  <label className='block text-sm font-medium text-slate-300 mb-2'>Endereco</label>
                  <input type='text' value={form.endereco} onChange={(e) => setForm({ ...form, endereco: e.target.value })} className={`w-full px-4 py-2.5 ${campo} rounded-lg focus:outline-none`} />
                </div>
                <div>
                  <label className='block text-sm font-medium text-slate-300 mb-2'>Operadora Doadora</label>
                  {(() => {
                    const knownCarriers = ['ALGAR', 'VIVO', 'CLARO', 'OI', 'TIP', 'SYSSVOIP', 'DATORA'];
                    const selectVal = knownCarriers.includes(form.operadoraDoadora) ? form.operadoraDoadora : (form.operadoraDoadora ? '_outro' : '');
                    const showCustomInput = selectVal === '_outro';
                    return (<>
                      <select value={selectVal} onChange={(e) => setForm({ ...form, operadoraDoadora: e.target.value === '_outro' ? (form.operadoraDoadora && !knownCarriers.includes(form.operadoraDoadora) ? form.operadoraDoadora : '_outro') : e.target.value })} className={`w-full px-4 py-2.5 ${campo} rounded-lg focus:outline-none`}>
                        <option value=''>Selecione...</option>
                        {knownCarriers.map(c => <option key={c} value={c}>{c}</option>)}
                        <option value='_outro'>Outro</option>
                      </select>
                      {showCustomInput && (
                        <input type='text' value={form.operadoraDoadora === '_outro' ? '' : form.operadoraDoadora} onChange={(e) => setForm({ ...form, operadoraDoadora: e.target.value.toUpperCase() || '_outro' })} placeholder='Digite o nome da operadora...' autoFocus className={`w-full mt-2 px-4 py-2.5 rounded-lg focus:outline-none placeholder:text-slate-500 ${campo}`} />
                      )}
                    </>);
                  })()}
                </div>
              </div>

              {/* Numeros a portar */}
              <div className='border-t border-central-borda pt-4'>
                <h4 className='text-sm font-semibold text-white mb-3'>Numeros a Portar</h4>

                {(ddrsErro || clienteErro) && (
                  <div role='alert' className={`mb-3 text-xs space-y-1 ${TONE_TEXT.warning}`}>
                    {clienteErro && <p>{fraseDoClienteQueNaoCarregou(clienteErro)}</p>}
                    {ddrsErro && <p>{fraseDosDdrsQueNaoCarregaram(ddrsErro)}</p>}
                  </div>
                )}
                {/* DDRs do cliente (checkboxes) */}
                {draftDDRs.length > 0 && (
                  <div className='mb-4'>
                    <p className='text-xs text-slate-400 mb-2'>DDRs do cliente (clique para selecionar):</p>
                    <div className='grid grid-cols-2 md:grid-cols-3 gap-2'>
                      {draftDDRs.map((ddr: any) => {
                        const isSelected = form.numeros.some((n: any) => n.numero === ddr.phoneNumber);
                        return (
                          <button
                            key={ddr.id}
                            type='button'
                            onClick={() => toggleDDR(ddr)}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-colors ${
                              isSelected
                                ? 'bg-primary/15 border-primary text-white'
                                : 'bg-slate-900 border-slate-700 text-slate-400 hover:border-slate-500'
                            }`}
                          >
                            <span className={`w-4 h-4 rounded border flex items-center justify-center ${isSelected ? 'bg-primary border-primary' : 'border-slate-600'}`}>
                              {isSelected && <Icon name='check' className='w-3 h-3 text-white' />}
                            </span>
                            <span className='font-mono'>{ddr.phoneNumber}</span>
                            <span className='text-xs text-slate-500'>({ddr.carrier})</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Lista de numeros adicionados */}
                {form.numeros.length > 0 && (
                  <div className='space-y-2 mb-3'>
                    {form.numeros.map((num: any, idx: number) => (
                      <div key={idx} className='flex items-center gap-3 p-3 bg-central-fundo/60 rounded-lg border border-central-borda'>
                        <select value={num.tipo} onChange={(e) => updateNumero(idx, 'tipo', e.target.value)} className={`px-3 py-1.5 text-sm rounded-lg ${campo}`}>
                          <option value='unico'>Unico</option>
                          <option value='sequencial'>Sequencial</option>
                          <option value='avulso'>Avulso</option>
                        </select>

                        {num.tipo === 'unico' && (
                          <input type='text' value={num.numero || ''} onChange={(e) => updateNumero(idx, 'numero', e.target.value)} placeholder='(45) 3122-5150' className={`flex-1 px-3 py-1.5 text-sm rounded-lg font-mono ${campo}`} />
                        )}
                        {num.tipo === 'sequencial' && (
                          <>
                            <input type='text' value={num.numeroInicial || ''} onChange={(e) => updateNumero(idx, 'numeroInicial', e.target.value)} placeholder='Inicial' className={`flex-1 px-3 py-1.5 text-sm rounded-lg font-mono ${campo}`} />
                            <span className='text-slate-500'>a</span>
                            <input type='text' value={num.numeroFinal || ''} onChange={(e) => updateNumero(idx, 'numeroFinal', e.target.value)} placeholder='Final' className={`flex-1 px-3 py-1.5 text-sm rounded-lg font-mono ${campo}`} />
                          </>
                        )}
                        {num.tipo === 'avulso' && (
                          <input type='text' value={(num.numerosAvulsos || []).join(', ')} onChange={(e) => updateNumero(idx, 'numerosAvulsos', e.target.value.split(',').map((s: string) => s.trim()).filter(Boolean))} placeholder='Numeros separados por virgula' className={`flex-1 px-3 py-1.5 text-sm rounded-lg font-mono ${campo}`} />
                        )}

                        <button type='button' onClick={() => removeNumero(idx)} className='p-1.5 text-slate-400 hover:text-red-400'>
                          <Icon name='trash' className='w-4 h-4' />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className='flex gap-2 flex-wrap'>
                  <button type='button' onClick={() => addManualNumber('unico')} className='px-3 py-1.5 text-xs bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600'>+ Numero Unico</button>
                  <button type='button' onClick={() => addManualNumber('sequencial')} className='px-3 py-1.5 text-xs bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600'>+ Faixa Sequencial</button>
                  <button type='button' onClick={() => addManualNumber('avulso')} className='px-3 py-1.5 text-xs bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600'>+ Avulsos</button>
                  <button type='button' onClick={() => setPasteMode(!pasteMode)} className='px-3 py-1.5 text-xs bg-slate-700 text-slate-200 rounded-lg hover:bg-slate-600 border border-slate-600'>
                    <Icon name='clipboard' className='w-3 h-3 inline mr-1' />Colar Lista
                  </button>
                </div>
                {pasteMode && (
                  <div className='mt-3 p-3 bg-central-fundo border border-central-borda rounded-lg'>
                    <p className='text-xs text-slate-400 mb-2'>Cole a lista de numeros (um por linha). Faixas sequenciais serao detectadas automaticamente.</p>
                    <textarea
                      value={pasteText}
                      onChange={(e) => setPasteText(e.target.value)}
                      placeholder={'8336900671\n8336900672\n8336900673\n...'}
                      rows={6}
                      autoFocus
                      className={`w-full px-3 py-2 text-sm rounded-lg font-mono resize-none focus:outline-none placeholder:text-slate-600 ${campo}`}
                    />
                    <div className='flex items-center justify-between mt-2'>
                      <span className='text-xs text-slate-500'>{pasteText.split(/[\n,;]+/).filter(s => s.replace(/\D/g, '').length >= 8).length} numeros detectados</span>
                      <div className='flex gap-2'>
                        <button type='button' onClick={() => { setPasteMode(false); setPasteText(''); }} className='px-3 py-1.5 text-xs bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600'>Cancelar</button>
                        <button type='button' onClick={() => parseNumberList(pasteText)} disabled={!pasteText.trim()} className='px-3 py-1.5 text-xs bg-primary text-white rounded-lg hover:bg-primary-strong disabled:opacity-50'>Processar</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Signatario */}
              <div className='border-t border-central-borda pt-4'>
                <h4 className='text-sm font-semibold text-white mb-3'>Signatario (responsavel do cliente)</h4>
                <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                  <div>
                    <label className='block text-sm font-medium text-slate-300 mb-2'>Nome *</label>
                    <input type='text' value={form.signatarioNome} onChange={(e) => setForm({ ...form, signatarioNome: e.target.value })} className={`w-full px-4 py-2.5 ${campo} rounded-lg focus:outline-none`} required />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-slate-300 mb-2'>CPF</label>
                    <input type='text' value={form.signatarioCpf} onChange={(e) => setForm({ ...form, signatarioCpf: e.target.value })} className={`w-full px-4 py-2.5 ${campo} rounded-lg focus:outline-none`} />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-slate-300 mb-2'>Email</label>
                    <input type='email' value={form.signatarioEmail} onChange={(e) => setForm({ ...form, signatarioEmail: e.target.value })} className={`w-full px-4 py-2.5 ${campo} rounded-lg focus:outline-none`} />
                  </div>
                </div>
                <div className='mt-4'>
                  <label className='block text-sm font-medium text-slate-300 mb-2'>Data de Autorizacao</label>
                  <input type='date' value={form.dataAutorizacao} onChange={(e) => setForm({ ...form, dataAutorizacao: e.target.value })} className={`px-4 py-2.5 ${campo} rounded-lg focus:outline-none`} />
                </div>
              </div>
        </CascaDeModal>
      )}

      {/* Modal View PDF (componente compartilhado — ver shared/DocumentViewerModal.tsx) */}
      {modal === 'view' && selectedTerm && (
        <DocumentViewerModal
          title={selectedTerm.numero}
          subtitle={selectedTerm.razaoSocial}
          pdfBase64={pdfPreview}
          loading={!pdfPreview && !previewErro}
          unsupportedMessage={previewErro ? `Não consegui carregar o PDF: ${previewErro}.` : undefined}
          accent='primary'
          onClose={() => setModal(null)}
          info={infoDoTermo(selectedTerm)}
          signers={selectedTerm.signers || []}
          documentSignedAt={selectedTerm.signedAt}
          signatureUrl={selectedTerm.clicksignSignatureUrl}
          timeline={termHistory}
          timelineLoading={historyLoading}
          sidebarExtra={historicoErro ? (
            <p role='status' className={`text-xs ${TONE_TEXT.warning}`}>Não consegui carregar o histórico: {historicoErro}.</p>
          ) : undefined}
          headerActions={
            <button
              onClick={() => handleDownloadPdf(selectedTerm)}
              className='flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200'
              title={rotuloDoPdf(selectedTerm.status).titulo}
            >
              <Icon name='download' className='w-4 h-4' />
              {rotuloDoPdf(selectedTerm.status).curto}
            </button>
          }
          sidebarActions={
            selectedTerm.status === 'Aguardando Assinatura' ? (
              <>
                <button
                  onClick={() => reenviarLembrete(selectedTerm.id)}
                  className='flex-1 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg text-xs transition-colors text-center'
                >
                  Reenviar Email
                </button>
                <button
                  onClick={() => handleCheckStatus(selectedTerm)}
                  className='flex-1 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg text-xs transition-colors text-center'
                >
                  Verificar Status
                </button>
              </>
            ) : null
          }
        />
      )}

      {/* Modal Signature */}
      {modal === 'signature' && selectedTerm && (
        // Signatário digitado: não fecha ao clicar fora, como já era. Envio para fora
        // (ClickSign): só moldura, handleSendForSignature não muda. O roxo da
        // primária e do foco dos campos vira o laranja.
        <CascaDeModal
          largura='xs'
          titulo='Enviar para Assinatura'
          subtitulo={selectedTerm.numero}
          onClose={() => setModal(null)}
          corpoClassName='p-6 space-y-4'
          rodape={
            <div className='ml-auto flex gap-3'>
              <button onClick={() => setModal(null)} className='px-4 py-2.5 text-sm font-medium text-slate-300 bg-slate-700 rounded-lg hover:bg-slate-600'>
                Cancelar
              </button>
              <button
                onClick={handleSendForSignature}
                disabled={saving || !signerForm.name || !signerForm.email}
                className='px-4 py-2.5 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-strong disabled:opacity-50'
              >
                {saving ? 'Enviando...' : 'Enviar para Assinatura'}
              </button>
            </div>
          }
        >
              <p className='text-sm text-slate-400'>
                O termo <span className='text-white font-medium'>{selectedTerm.numero}</span> sera enviado para assinatura digital via ClickSign.
              </p>
              <p className='text-xs text-slate-500'>O signatario e o responsavel do <b>cliente</b> que autoriza a portabilidade.</p>

              <div>
                <label className='block text-sm font-medium text-slate-300 mb-2'>Nome do Signatario *</label>
                <input type='text' value={signerForm.name} onChange={(e) => setSignerForm({ ...signerForm, name: e.target.value })} className={`w-full px-4 py-2.5 ${campo} rounded-lg focus:outline-none`} />
              </div>

              <div>
                <label className='block text-sm font-medium text-slate-300 mb-2'>Email *</label>
                <input type='email' value={signerForm.email} onChange={(e) => setSignerForm({ ...signerForm, email: e.target.value })} className={`w-full px-4 py-2.5 ${campo} rounded-lg focus:outline-none`} />
              </div>

              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label className='block text-sm font-medium text-slate-300 mb-2'>CPF</label>
                  <input type='text' value={signerForm.cpf} onChange={(e) => setSignerForm({ ...signerForm, cpf: e.target.value })} className={`w-full px-4 py-2.5 ${campo} rounded-lg focus:outline-none`} />
                </div>
                <div>
                  <label className='block text-sm font-medium text-slate-300 mb-2'>Metodo de Assinatura</label>
                  <select value={signerForm.authType} onChange={(e) => setSignerForm({ ...signerForm, authType: e.target.value as any })} className={`w-full px-4 py-2.5 ${campo} rounded-lg focus:outline-none`}>
                    <option value='email'>Codigo por Email</option>
                    <option value='whatsapp'>Codigo por WhatsApp</option>
                    <option value='icp_brasil'>Certificado Digital (ICP-Brasil)</option>
                  </select>
                </div>
              </div>
              {signerForm.authType === 'whatsapp' && (
                <div className='mt-4'>
                  <label className='block text-sm font-medium text-slate-300 mb-2'>WhatsApp *</label>
                  <input
                    type='text'
                    value={signerForm.phone}
                    onChange={(e) => setSignerForm({ ...signerForm, phone: e.target.value })}
                    placeholder='(45) 99999-9999'
                    className={`w-full px-4 py-2.5 ${campo} rounded-lg focus:outline-none`}
                  />
                </div>
              )}
        </CascaDeModal>
      )}
    </div>
  );
}
